"use client";

import "@/app/admin/admin.css";

import { memo, type FormEvent, type ReactNode } from "react";
import { useTranslations } from "next-intl";

import {
  AdminDialogHeader,
  DialogCancelButton,
  DialogConfirmButton,
  DialogDeleteButton,
  SettingsSaveButton,
  type SettingsActionVariant,
} from "@/components/admin/settings-form-controls";
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ADMIN_DIALOG_WIDTH_CLASS,
  adminDialogBodyClass,
  adminDialogContentClass,
} from "@/hooks/use-admin-dialog";
import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

/** Shared dialog tabs: the channel editor's full-width header and independently scrolling body. */
export function AdminDialogTabs({ value, onValueChange, tabs, children, className }: {
  value: string;
  onValueChange: (value: string) => void;
  tabs: readonly { value: string; label: ReactNode; disabled?: boolean }[];
  children: ReactNode;
  className?: string;
}) {
  return <Tabs value={value} onValueChange={next => onValueChange(String(next))} className={cn("flex h-[460px] min-h-0 flex-col", className)}>
    <TabsList className="w-full shrink-0 justify-start">{tabs.map(tab => <TabsTrigger key={tab.value} value={tab.value} disabled={tab.disabled}>{tab.label}</TabsTrigger>)}</TabsList>
    <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">{children}</div>
  </Tabs>;
}

export function AdminDialogBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn(adminDialogBodyClass, className)}>{children}</div>
  );
}

const ADMIN_DIALOG_SIZE_CLASS = ADMIN_DIALOG_WIDTH_CLASS;

export type AdminDialogSize = keyof typeof ADMIN_DIALOG_SIZE_CLASS;

export type AdminDialogCancelProps = {
  label?: string;
  disabled?: boolean;
  variant?: SettingsActionVariant;

  onClick?: () => void;
};

export type AdminDialogConfirmProps = {
  label?: string;
  variant?: SettingsActionVariant;

  savingLabel?: string;
  disabled?: boolean;

  dirty?: boolean;

  destructive?: boolean;
  type?: "button" | "submit";
  onClick?: () => void;
};

export type AdminDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  title: ReactNode;

  subtitle?: ReactNode;

  size?: AdminDialogSize;

  className?: string;

  bodyClassName?: string;

  rawBody?: boolean;

  saving?: boolean;

  cancel?: AdminDialogCancelProps | false;

  confirm?: AdminDialogConfirmProps;

  footer?: ReactNode;

  footerClassName?: string;

  submitOnEnter?: boolean;
  children?: ReactNode;
};

function AdminDialogConfirmButton({
  confirm,
  saving,
}: {
  confirm: AdminDialogConfirmProps;
  saving: boolean;
}) {
  const t = useTranslations("site.ui");

  if (confirm.destructive) {
    return (
      <DialogDeleteButton
        variant={confirm.variant}
        saving={saving}
        disabled={confirm.disabled}
        confirmLabel={confirm.label}
        deletingLabel={confirm.savingLabel}
        type={confirm.type}
        onClick={confirm.onClick}
      />
    );
  }

  const label = confirm.label ?? t("confirm");
  const content =
    saving && confirm.savingLabel ? confirm.savingLabel : label;

  if (confirm.dirty !== undefined) {
    return (
      <SettingsSaveButton
        variant={confirm.variant}
        dirty={confirm.dirty}
        saving={saving}
        type={confirm.type ?? "button"}
        onClick={confirm.onClick}
      >
        {content}
      </SettingsSaveButton>
    );
  }

  return (
    <DialogConfirmButton
      variant={confirm.variant}
      saving={saving}
      disabled={confirm.disabled}
      type={confirm.type}
      onClick={confirm.onClick}
    >
      {content}
    </DialogConfirmButton>
  );
}

const FreezeWhileClosing = memo(
  function FreezeWhileClosing({ children }: { open: boolean; children: ReactNode }) {
    return <>{children}</>;
  },
  (_previous, next) => !next.open,
);

export function AdminDialog({
  open,
  onOpenChange,
  title,
  subtitle,
  size = "md",
  className,
  bodyClassName,
  rawBody = false,
  saving = false,
  cancel,
  confirm,
  footer,
  footerClassName,
  submitOnEnter = false,
  children,
}: AdminDialogProps) {
  const showFooter = footer !== undefined || confirm !== undefined || Boolean(cancel);
  const showCancel = cancel !== false;
  const cancelProps = cancel === false ? undefined : cancel;
  const submitOnEnterEnabled = Boolean(submitOnEnter && confirm?.onClick);

  function handleFormSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (saving || confirm?.disabled || !confirm?.onClick) {
      return;
    }

    confirm.onClick();
  }

  const confirmForRender =
    confirm && submitOnEnterEnabled
      ? { ...confirm, type: "submit" as const, onClick: undefined }
      : confirm;

  const bodyNode = rawBody ? (
    children
  ) : (
    <AdminDialogBody className={bodyClassName}>{children}</AdminDialogBody>
  );

  const footerNode =
    showFooter && (footer !== undefined || confirmForRender || showCancel) ? (
      <DialogFooter className={footerClassName}>
        {footer ?? (
          <>
            {showCancel ? (
              <DialogCancelButton
                variant={cancelProps?.variant}
                saving={saving}
                disabled={cancelProps?.disabled}
                onClick={cancelProps?.onClick ?? (() => onOpenChange(false))}
              >
                {cancelProps?.label}
              </DialogCancelButton>
            ) : null}
            {confirmForRender ? (
              <AdminDialogConfirmButton
                confirm={confirmForRender}
                saving={saving}
              />
            ) : null}
          </>
        )}
      </DialogFooter>
    ) : null;

  const mainContent = (
    <>
      {bodyNode}
      {footerNode}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-admin-dialog
        className={cn(
          adminDialogContentClass,
          ADMIN_DIALOG_SIZE_CLASS[size],
          className,
        )}
      >
        <FreezeWhileClosing open={open}>
          <AdminDialogHeader title={title} subtitle={subtitle} />

          {submitOnEnterEnabled ? (
            <form className="contents" onSubmit={handleFormSubmit}>
              {mainContent}
            </form>
          ) : (
            mainContent
          )}
        </FreezeWhileClosing>
      </DialogContent>
    </Dialog>
  );
}
